import { DEFAULT_PRICING } from "@/lib/commercial-config";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { Sparkles, Check, Loader2 } from "lucide-react";
import { createCheckoutSession } from "@/utils/payments.functions";
import { useAuth } from "@/hooks/use-auth";
import { useCommercialConfig, usePaymentsAvailability } from "@/lib/pricing-labels";
import { planLimitLine } from "@/lib/plan-copy";
import { getStripeEnvironment } from "@/lib/stripe";
import { arePaymentsAvailable } from "@/lib/native-commerce-flags";
import { PaymentsUnavailableNotice } from "@/components/PaymentsUnavailableNotice";
import { getStoredAffiliateCode, getStoredAffiliateSlug } from "@/lib/affiliate-ref";

export function ProPaywall() {
  const { user } = useAuth();
  const createCheckout = useServerFn(createCheckoutSession);
  const commercial = useCommercialConfig();
  const payments = usePaymentsAvailability();
  const proLabel = (commercial.pricing.pro_monthly ?? DEFAULT_PRICING.pro_monthly).label;
  const features = [
    "Inclui todas as vantagens do Routify Premium",
    "Roteiros multi-dia e multi-cidade com otimização de rota",
    "Adaptação ao meio de transporte (a pé, bicicleta, carro, transportes)",
    "Paragens automáticas para refeições no horário certo",
    "Descrições personalizadas geradas por IA para cada dia",
    "Regeneração de qualquer dia sem refazer tudo",
    planLimitLine("pro", commercial.limits, "pt"),
  ];
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  async function handleSubscribe() {
    if (!user || checkoutOpen) return;
    if (!payments.available) {
      alert(payments.reason ?? "Pagamentos indisponíveis neste ambiente.");
      return;
    }
    setCheckoutOpen(true);
    try {
      const result = await createCheckout({
        data: {
          priceId: "pro_monthly",
          customerEmail: user.email,
          userId: user.id,
          returnUrl: `${window.location.origin}/checkout/return?plan=pro&session_id={CHECKOUT_SESSION_ID}`,
          upgradeReturnUrl: `${window.location.origin}/checkout/return?plan=pro&upgraded=1`,
          cancelUrl: `${window.location.origin}/pro`,
          environment: getStripeEnvironment(),
          uiMode: "hosted",
          affiliateCode: getStoredAffiliateCode() ?? undefined,
          affiliateSlug: getStoredAffiliateSlug() ?? undefined,
        },
      });
      if ("error" in result) throw new Error(result.error);
      if (!result.url) throw new Error("Stripe não devolveu o link de pagamento");
      window.location.assign(result.url);
    } catch (error) {
      setCheckoutOpen(false);
      alert(error instanceof Error ? error.message : "Não foi possível abrir o pagamento");
    }
  }

  return (
    <div className="rounded-2xl border border-border bg-gradient-to-br from-brand/5 via-card to-amber-500/5 p-8 shadow-sm">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">
          <Sparkles className="h-3.5 w-3.5" /> Routify Pro
        </div>
        <h2 className="mt-4 text-3xl font-bold">Planeador inteligente sem stress</h2>
        <p className="mt-2 text-muted-foreground">
          A IA faz todo o trabalho pesado — tu só tens de viajar.
        </p>
        <div className="mt-6">
          <span className="text-5xl font-bold">{proLabel}</span>
          <span className="text-muted-foreground">/mês</span>
        </div>
      </div>

      <ul className="mx-auto mt-8 max-w-md space-y-2 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 flex flex-col items-center gap-3">
        {!arePaymentsAvailable() ? (
          <PaymentsUnavailableNotice />
        ) : (
        <button
          onClick={handleSubscribe}
          disabled={!user || checkoutOpen}
          className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand/90 disabled:opacity-60"
        >
          {checkoutOpen || !user ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {checkoutOpen ? "A abrir pagamento…" : "Subscrever Routify Pro"}
        </button>
        )}
        <p className="text-xs text-muted-foreground">Cancela quando quiseres · Sem compromisso</p>
      </div>

      <div className="mt-8 border-t border-border pt-6 text-center">
        <p className="text-sm text-muted-foreground">
          Preferes algo feito à medida pela nossa equipa?
        </p>
        <Link to="/concierge" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline">
          Pedir planeamento personalizado →
        </Link>
      </div>
    </div>
  );
}
